'use client';
import { Box } from '@mui/material';

import { createRef, useEffect, useRef, useState } from 'react';
import {
    StyledTab,
    StyledTabs,
} from '@/components/shared/TabsThemed/tabs-themed.component';
import Typography from '@/components/shared/typography/typography.component';
import { CloseButtonIcon } from '../../../../../public/Images';
import { ModalTabsText } from '../StyledComponents/modal-components';
import ModalPropertValueSection, {
    IPropertyValueSection,
} from './modal-property-value-section.component';
import { useAppDispatch, useTranslation, useWindowDimensions } from '@/hook';

import NotifyMeButton from '../NotifyMe/Button/notify-me-button.component';
import styles from './modal-info.module.scss';
import Image from 'next/image';

import { getImperfectionReportIcon } from '..';

import { AllowedVehicleBookingTypes } from '@/types';
import React from 'react';
import Link from '@/components/WrappedLink';
import { toggleLoading } from '@/redux/reducers/checkoutReducer';


export interface IModalData {
    icon: string | React.ReactNode;
    name: string;
    isFaulty?: boolean;
    data: IPropertyValueSection;
    applyGallery: boolean;
}

const ModalInfo = ({
    data,
    open,
    setOpen,
    clickedItem,
    title,
    valueType,
    id,
    isReserved,
    isSold,
    bookingType,
}: {
    data: IModalData[];
    open: boolean;
    setOpen: (flag: boolean) => void;
    clickedItem: number;
    title: string;
    valueType: 'text' | 'text-with-comment';
    id?: string;
    isReserved?: boolean;
    isSold?: boolean;
    bookingType: AllowedVehicleBookingTypes;
}) => {
    const [tabScrollIndex, setTabScrollIndex] = useState<number>(0);
    const [value, setValue] = useState(clickedItem);
    const dispatch = useAppDispatch();

    const itemsRefs = useRef<any>(
        Array.from({ length: data.length as number }, () => createRef())
    );
    const mainRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLDivElement>(null);
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const { IS_DESKTOP } = useWindowDimensions();

    const getDimensions = (ele: HTMLDivElement) => {
        const { height } = ele.getBoundingClientRect();
        const offsetTop = ele.offsetTop;
        const offsetBottom = offsetTop + height;
        return {
            height,
            offsetTop,
            offsetBottom,
        };
    };

    const handleScroll = () => {
        if (!mainRef?.current || !headerRef?.current) return;
        const { offsetBottom } = getDimensions(headerRef?.current);
        const margins = 16 * 1.5 * data.length;
        const scrollPosition =
            mainRef.current.scrollTop + margins + offsetBottom;
        itemsRefs?.current?.forEach((ref: { current: any }, index: number) => {
            const ele = ref.current;

            if (ele) {
                const { offsetBottom, offsetTop } = getDimensions(ele);
                if (
                    scrollPosition > offsetTop &&
                    scrollPosition < offsetBottom
                ) {
                    setValue(index);
                    // setTabScrollIndex(index);
                }
            }
        });
    };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        setTabScrollIndex(newValue);
    };
    useEffect(() => {
        itemsRefs.current[tabScrollIndex]?.current?.scrollIntoView({
            block: 'start',
        });
    }, [tabScrollIndex]);
    useEffect(() => {
        if (!open) {
            setTabScrollIndex(0);
            setValue(0);
        }

        setTabScrollIndex(clickedItem);
        return () => {
            itemsRefs.current[0]?.current?.scrollIntoView({
                block: 'start',
            });
        };
    }, [open]);

    const headerContainer = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // marginTop: '16px',
        width: '100%',
        marginBottom: '8px',
    };
    const overviewText = {
        color: '#00172F',
        lineHeight: '20px',
        opacity: 1,
    };

    const reserveCarButtonClicked = () => {
        if (id === undefined) return;
        // navigate(`/${i18n.language}/checkout/reserve?carId=${id}`);
    };

    function appHeight() {
        const doc = document.documentElement;
        doc.style.setProperty('--vh', window.innerHeight * 0.01 + 'px');
    }

    window.addEventListener('resize', appHeight);
    appHeight();

    return (
        <Box className={styles.container}>
            <Box ref={headerRef}>
                <Box sx={headerContainer}>
                    <Typography
                        style={overviewText}
                        variant={'subtitle2Medium'}
                    >
                        {' '}
                        {title}{' '}
                    </Typography>
                    <Box
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                            setTabScrollIndex(0);
                            setValue(0);
                            setOpen(false);
                        }}
                    >
                        <Image src={CloseButtonIcon} alt={CloseButtonIcon} />
                    </Box>
                </Box>
                <Box>
                    <StyledTabs
                        // className={classes.root}
                        value={value}
                        onChange={handleChange}
                        variant="scrollable"
                        scrollButtons={false}
                    >
                        {data?.map(item => {
                            return (
                                <StyledTab
                                    isFaulty={item.isFaulty}
                                    label={
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                            }}
                                        >
                                            {typeof item.icon == 'string'
                                                ? getImperfectionReportIcon(
                                                      item.icon
                                                  )
                                                : item.icon}
                                            <ModalTabsText>
                                                {item.name}
                                            </ModalTabsText>
                                        </Box>
                                    }
                                    key={item.name}
                                />
                            );
                        })}
                    </StyledTabs>
                </Box>
            </Box>
            <div
                onScroll={() => handleScroll()}
                ref={mainRef}
                className={styles.innerBody}
            >
                {data.map((item, index) => (
                    <div key={item.data.title} ref={itemsRefs?.current[index]}>
                        <ModalPropertValueSection
                            applyGallery={item.applyGallery}
                            valueType={valueType}
                            data={item.data}
                        />
                    </div>
                ))}
            </div>
            {!IS_DESKTOP && !isSold && (
                <Box
                    sx={{
                        position: 'sticky',
                        left: 0,
                        right: 0,
                        width: '100%',
                        bottom: 0,
                        overflow: 'scroll',
                        background: 'white',
                        padding: '16px 12px',
                        boxShadow: '0px 24px 48px -12px rgba(16, 24, 40, 0.18)',
                        borderTop: '1px solid #EAECF0',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        height: '98px',
                    }}
                >
                    {bookingType !== AllowedVehicleBookingTypes.SEE_ONLY &&
                        (isReserved ? (
                            id && (
                                <NotifyMeButton
                                    closeOtherBottomSheet={setOpen}
                                    carId={id}
                                />
                            )
                        ) : (
                            <Link
                                href={`/${lang}/car-details/${id}/checkout/reserve`}
                                onClick={() => {
                                    reserveCarButtonClicked();
                                    dispatch(toggleLoading(false));
                                 }
                                }
                                style={{ height: '80%' }}
                                className={`sylndr_button primary ${
                                    isReserved && 'disabled'
                                }`}
                            >
                                {t('reserve car')}
                            </Link>
                        ))}
                    {bookingType !==
                        AllowedVehicleBookingTypes.RESERVE_ONLY && (
                        <Link
                            href={`/${lang}/car-details/${id}/checkout/see`}
                            style={{ height: '80%' }}
                            className="sylndr_button secondary"
                            onClick={() =>  dispatch(toggleLoading(false))}
                        >
                            {t('See Car')}
                        </Link>
                    )}
                </Box>
            )}
        </Box>
    );
};
export default ModalInfo;
